import { useEffect, useMemo, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  Typography,
  Button,
  Row,
  Col,
  Divider,
  Dropdown,
  Menu,
  notification,
} from "antd";
import { journalsQueries } from "@/entities/journal";
import { $journalApi } from "@/entities/journal/fetch/fetch-client";
import { useValidateTitle } from "@/features/journal-title-validation";
import { RepresentativeModalOrgs } from "@/features/representation-orgs-modal";
import { BasketIcon, MoreOneIcon } from "@/shared/assets/svg/icons";
import { Can } from "@/shared/lib/casl";
import {
  useCoordinator,
  useDisclosure,
  useResetState,
} from "@/shared/lib/hooks";
import { queryClient } from "@/shared/lib/react-query";
import {
  fallbackString,
  formatDate,
  NOTIFICATION_MESSAGES,
} from "@/shared/utils";
import type { components } from "@entities/journal";
import { ParticipantsInfiniteScroll } from "@features/participants-infinite-scroll";
import { useGetRepresentative } from "../../api";
import {
  useCreateParticipant,
  useDeleteParticipant,
  useTitleAmendmentMode,
} from "../../model";
import { CollapsibleSection } from "../collapsible-section";
import { SupervisionsTable } from "../journals-supervision-table/ui";
import { createValidationErrorsGetter } from "../journals-title/model/get-validation-errors-for-section";
import { RepresentativeWrapper } from "../representative-wrapper";
import { RepresentativesSignatureWrapper } from "../representatives-signature-wrapper";
import { SroListContainer } from "../sro-list-container";

export function DesignerSupervisionsTableNew() {
  const [selectedOrganization, setSelectedOrganization] = useState<{
    designerSupervisionListId?: string;
    projectParticipantId?: string;
    company?: components["schemas"]["Company"];
    representativeListId?: string;
    representatives?: components["schemas"]["DesignerSupervisionRepresentative"][];
  } | null>(null);
  const { key, reset } = useResetState();
  const { projectId, titleId } = useCoordinator();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { errorList } = useValidateTitle();

  const { representative: designerSupervisions, isFetching } =
    useGetRepresentative("designerSupervisions");

  const validationErrors = useMemo(() => {
    if (!designerSupervisions || designerSupervisions.length === 0) {
      return [];
    }
    return errorList.filter((error) => [18].includes(error.id));
  }, [errorList, designerSupervisions]);

  const getValidationErrorsForSection = useMemo(
    () => createValidationErrorsGetter(errorList),
    [errorList],
  );

  const { data } = useQuery(journalsQueries.getTitleJournal({ titleId }));
  const titleStatus =
    data?.mainJournal?.titleJournal?.status === "NEW" &&
    // @ts-expect-error versionNumber отсутствует в openapi типе TitleJournalViewDto
    !data?.mainJournal?.titleJournal?.versionNumber;
  const { isNewAmendment } = useTitleAmendmentMode();
  const canAddData = titleStatus || isNewAmendment;

  const {
    mutate: createParticipant,
    isPending: isLoadingCreate,
    isSuccess: isSuccessCreate,
  } = useCreateParticipant({ titleId });

  const { mutate: deleteParticipant, isPending: isLoadingDelete } =
    useDeleteParticipant({ titleId });

  const { mutate: removeParticipant } = $journalApi.useMutation(
    "delete",
    "/api/v1/journals/title/{titleId}/representatives",
    {
      body: (data: any) => data,
      onSuccess: (data) => {
        const designerSupervisionsErrors =
          data?.errorsList?.designerSupervisions;
        if (
          Array.isArray(designerSupervisionsErrors) &&
          designerSupervisionsErrors.length > 0 &&
          designerSupervisionsErrors[0]?.representatives?.[0]?.errorMessage
        ) {
          notification.error({
            message: NOTIFICATION_MESSAGES.ERROR, // "Ошибка"
            description:
              designerSupervisionsErrors[0]?.representatives?.[0]
                ?.errorMessage ??
              NOTIFICATION_MESSAGES.PARTICIPANT_DELETE_FAILED_DESC, // "Не удалось удалить участника"
          });
        } else {
          notification.success({
            message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
            description: NOTIFICATION_MESSAGES.PARTICIPANT_DELETED_DESC, // "Участник успешно удален"
          });
          queryClient.invalidateQueries({
            queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
          });
        }
      },
    },
  );

  const handleDeleteContractor = (id?: string) => {
    const listId = designerSupervisions[0]?.designerSupervisionListId;
    const updatedParticipants =
      designerSupervisions?.filter(
        (participant) => participant.projectParticipantId === id,
      ) || [];
    deleteParticipant({
      participantType: "designerSupervisions",
      projectParticipantId: updatedParticipants.map(
        (p) => p.projectParticipantId ?? "",
      ),
      listId,
    });
  };

  const handleDeleteRepresentative = (
    representativeId: string,
    participantId: string,
  ) => {
    removeParticipant({
      params: {
        path: {
          titleId,
        },
      },
      body: {
        designerSupervisions: [
          {
            projectParticipantId: participantId,
            representatives: [{ representativeId }],
          },
        ],
      },
    });
  };

  const onPartySelect = (participant: any) => {
    if (!participant) return;

    const newParticipant = {
      projectParticipantId: participant.projectParticipantId ?? "",
      company: {
        companyId: participant.company.companyId ?? "",
      },
    };

    createParticipant({
      participantType: "designerSupervisions",
      companyId: [newParticipant.company.companyId],
      projectParticipantId: [newParticipant.projectParticipantId],
    });
  };

  useEffect(() => {
    reset();
  }, [isSuccessCreate, reset]);

  return (
    <CollapsibleSection
      title="Авторский надзор"
      spinning={isFetching || isLoadingDelete}
      validationErrors={validationErrors}
      sectionId="designer-supervisions"
      getValidationErrorsForSection={getValidationErrorsForSection}
      isEmpty={!designerSupervisions || designerSupervisions.length === 0}
    >
      <>
        {designerSupervisions?.map((contractor) => {
          return (
            <div
              key={contractor.projectParticipantId}
              className="journal-title-participant-section"
            >
              <div className="journal-title-participant-header">
                <Typography.Title level={5}>
                  {fallbackString(contractor?.company?.fullName)}
                </Typography.Title>
                {titleStatus && !isNewAmendment ? (
                  <Can I="edit" a="title_journals">
                    <Dropdown
                      overlay={
                        <Menu>
                          <Menu.Item
                            key="delete"
                            icon={<BasketIcon />}
                            onClick={() =>
                              handleDeleteContractor(
                                contractor?.projectParticipantId,
                              )
                            }
                          >
                            Удалить
                          </Menu.Item>
                        </Menu>
                      }
                      trigger={["click"]}
                    >
                      <div className="journal-title-menu-trigger">
                        <MoreOneIcon width={24} height={24} />
                      </div>
                    </Dropdown>
                  </Can>
                ) : null}
              </div>
              <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
                <Col span={8} style={{ color: "#888" }}>
                  ИНН
                </Col>
                <Col span={16}>{fallbackString(contractor?.company?.inn)}</Col>
                <Col span={8} style={{ color: "#888" }}>
                  КПП
                </Col>
                <Col span={16}>{fallbackString(contractor?.company?.kpp)}</Col>
                <Col span={8} style={{ color: "#888" }}>
                  ОГРН
                </Col>
                <Col span={16}>{fallbackString(contractor?.company?.ogrn)}</Col>
                <Col span={8} style={{ color: "#888" }}>
                  Контакты
                </Col>
                <Col span={16}>
                  {contractor?.company?.contacts?.phone}
                  {fallbackString(contractor?.company?.contacts?.email)}{" "}
                </Col>
                <Col span={8} style={{ color: "#888" }}>
                  Адрес
                </Col>
                <Col span={16}>
                  {fallbackString(
                    contractor?.company?.addresses?.legal?.fullAddress,
                  )}
                </Col>
                <Col span={8} style={{ color: "#888" }}>
                  Выдано
                </Col>
                <Col span={16}>
                  {fallbackString(formatDate(contractor?.company?.ogrnDate))}
                </Col>
                <Col span={8} style={{ color: "#888" }}>
                  Дата выдачи
                </Col>
                <Col span={16}>
                  {fallbackString(formatDate(contractor?.company?.ogrnDate))}
                </Col>
              </Row>
              <Divider />

              <SroListContainer sro={contractor?.company?.sro} />

              <Divider />
              {contractor.representatives?.length
                ? contractor.representatives.map((rep) => (
                    <>
                      <RepresentativesSignatureWrapper
                        representativeType="designerSupervisions"
                        representative={rep}
                      >
                        <div
                          key={
                            rep.administrativeDocument?.administrativeDocumentId
                          }
                          className="journal-title-rep-card"
                        >
                          <div className="journal-title-rep-card__header">
                            <Typography.Title
                              level={5}
                              className="journal-title-rep-card__name"
                            >
                              {fallbackString(
                                `${rep.user?.surname || ""} ${rep.user?.firstName || ""} ${rep.user?.middleName || ""}`.trim(),
                              )}
                            </Typography.Title>
                            {rep.administrativeDocument
                              ?.administrativeDocumentId &&
                              titleStatus &&
                              !isNewAmendment && (
                                <Can I="edit" a="title_journals">
                                  <Dropdown
                                    overlay={
                                      <Menu>
                                        <Menu.Item
                                          key="delete"
                                          icon={<BasketIcon />}
                                          onClick={() => {
                                            handleDeleteRepresentative(
                                              rep.representativeId ?? "",
                                              contractor.projectParticipantId ??
                                                "",
                                            );
                                          }}
                                        >
                                          Удалить
                                        </Menu.Item>
                                      </Menu>
                                    }
                                    trigger={["click"]}
                                  >
                                    <div className="journal-title-menu-trigger">
                                      <MoreOneIcon width={24} height={24} />
                                    </div>
                                  </Dropdown>
                                </Can>
                              )}
                          </div>

                          <RepresentativeWrapper rep={rep} />
                          <div className="journal-title-doc-section-heading">
                            Сведения о разделах проектной документации,
                            подготовленных этим лицом
                          </div>
                          <SupervisionsTable
                            // @ts-expect-error projectSections отсутствует в openapi типе представителя
                            sections={rep?.projectSections || []}
                          />
                        </div>
                      </RepresentativesSignatureWrapper>
                    </>
                  ))
                : null}

              {canAddData ? (
                <Can I="edit" a="title_journals">
                  <div className="journal-title-add-employee-block">
                    <Button
                      key={`${key}-button`}
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setSelectedOrganization(contractor);
                        onOpen();
                      }}
                    >
                      Добавить сотрудника
                    </Button>
                  </div>
                </Can>
              ) : null}
            </div>
          );
        })}
        {canAddData ? (
          <Can I="edit" a="title_journals">
            <ParticipantsInfiniteScroll
              projectId={projectId}
              onPartySelect={onPartySelect}
              key={key}
              isLoading={isLoadingCreate}
            />
          </Can>
        ) : null}
        <RepresentativeModalOrgs
          isOpen={isOpen}
          handleClose={onClose}
          type="author"
          organization={{
            representativeType: "designerSupervisions",
            isConstructionControl: false,
            companyId: selectedOrganization?.company?.companyId ?? "",
            projectParticipantId:
              selectedOrganization?.projectParticipantId ?? "",
            userCompaniesHistoryId:
              selectedOrganization?.company?.companyHistoryId ?? "",
          }}
          isLoading={isLoadingCreate}
          isArray
          representativesAlreadyAdded={selectedOrganization?.representatives}
          enableMultiAdd
          key={`${key}-RepresentativeModalOrgs ${new Date().getTime()}`}
          isAddFromOldService
        />
      </>
    </CollapsibleSection>
  );
}
